import React from 'react';
import { Link } from 'react-router-dom';

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 text-center">TWINS Network Terms of Use</h1>
        
        <p className="text-gray-400 mb-8 text-center">Last Updated: December 2024</p>
        
        <div className="prose prose-invert max-w-none">
          <p className="mb-6">
            TWINS Network ("we", "us", "our") welcomes you to the TWINS Network platform, 
            including all websites, applications, and services provided by TWINS Network 
            (collectively the <strong>"Platform"</strong>).
          </p>
          
          <p className="mb-8">
            Please read these terms and conditions of use carefully before using our Platform.
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. ACCEPTANCE OF TERMS</h2>
            <p className="mb-4">
              By entering, accessing or using the Platform you acknowledge that you have read 
              and understood these Terms of Use and Privacy Notice (collectively, the <strong>"Terms"</strong>) 
              and you agree to be bound by them and to comply with all applicable laws and 
              regulations regarding your use of the Platform. These Terms are a binding and 
              enforceable legal contract between you and TWINS Network.
            </p>
            <p className="font-semibold">
              IF YOU DO NOT AGREE TO THESE TERMS, PLEASE DO NOT ACCESS OR USE THE PLATFORM.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. DESCRIPTION OF PLATFORM</h2>
            <p className="mb-4">
              TWINS Network provides an informational platform for viewing blockchain data, 
              masternode statistics, network information, and other data related to the TWINS 
              cryptocurrency ecosystem. The Platform includes:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-2">
              <li>Blockchain explorer functionality</li>
              <li>Masternode monitoring and statistics</li>
              <li>Network performance metrics</li>
              <li>Developer fund transparency</li>
              <li>Wallet release information</li>
            </ul>
            <p>
              The Platform is provided for informational purposes only and does not constitute 
              financial advice or recommendations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. ELIGIBILITY</h2>
            <p>
              By using the Platform, you represent and warrant that you:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-2">
              <li>Are at least 18 years old or of legal age in your jurisdiction</li>
              <li>Have the legal capacity to enter into these Terms</li>
              <li>Have not been previously banned from using our Platform</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. DISCLAIMER OF WARRANTIES</h2>
            <p className="mb-4">
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, 
              ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>
            <p>
              We strive to maintain accurate information on the Platform but cannot guarantee 
              the accuracy, completeness, or timeliness of any data displayed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. LIMITATION OF LIABILITY</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, TWINS NETWORK SHALL NOT BE LIABLE FOR ANY 
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF 
              PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, 
              USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. RISK WARNING</h2>
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 mb-4">
              <p className="font-semibold mb-2">
                CRYPTOCURRENCY INVESTMENTS CARRY SIGNIFICANT RISKS:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Digital asset values can fluctuate dramatically</li>
                <li>You may lose some or all of your investment</li>
                <li>Past performance does not guarantee future results</li>
                <li>Technical risks include hacking, software bugs, and network failures</li>
              </ul>
            </div>
            <p>
              You acknowledge and accept all risks associated with cryptocurrencies and 
              blockchain technology. TWINS Network is not responsible for any losses you 
              may incur.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. INTELLECTUAL PROPERTY</h2>
            <p>
              All content on the Platform, including text, graphics, logos, and software, 
              is the property of TWINS Network or its licensors and is protected by 
              intellectual property laws. You may not reproduce, distribute, or create 
              derivative works without our express written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. PRIVACY</h2>
            <p>
              Your use of the Platform is subject to our Privacy Policy. By using the 
              Platform, you consent to our collection and use of information as described 
              in the Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. MODIFICATIONS TO TERMS</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users 
              of any material changes by updating the "Last Updated" date. Your continued 
              use of the Platform after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. GOVERNING LAW</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws 
              of the jurisdiction in which TWINS Network operates, without regard to its 
              conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. CONTACT INFORMATION</h2>
            <p>
              If you have questions about these Terms, please contact us through our 
              official channels:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2">
              <li>Website: <a href="https://win.win" className="text-blue-400 hover:text-blue-300">https://win.win</a></li>
              <li>Community: Join our official Discord or Telegram channels</li>
            </ul>
          </section>

          <div className="mt-12 p-6 bg-gray-800 rounded-lg">
            <p className="text-center font-semibold">
              BY USING THE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, 
              AND AGREE TO BE BOUND BY THESE TERMS OF USE.
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-blue-400 hover:text-blue-300">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Terms; 